import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  Renderer2,
  signal,
  Signal,
  untracked,
  ViewChild,
} from '@angular/core';
import {
  WorkDocument,
  WorkDocumentProcessingStatusEnum,
  WorkDocumentsV2Service,
  WorkPermissionsEnum,
  WorkUsersV2Service,
  WorkUserV2,
} from '@work-frontend-monorepo/work-rest-api';
import { ButtonComponent } from '../button/button.component';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { ExceptionHandlingService } from '../../services/exception-handling/exception-handling.service';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';
import { ConfirmActionModalDirective } from '../../directives/confirm-action-modal/confirm-action-modal.directive';
import { AlertDialogService } from '../../services/alert-dialog/alert-dialog.service';
import { environment } from '@work-frontend-monorepo/work-environments';
import { TranslateModule } from '@ngx-translate/core';
import { StringMap } from '../../models/string-map.type';
import { IconComponent } from '../icon/icon.component';
import { PrependServerToFileSrcPipe } from '../../pipes/prepend-server-to-file-src.pipe';
import { FormatDatePipe } from '../../pipes/format-date.pipe';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import { ProfileState } from '../../state/states/profile/profile-state';
import { ImageAnnotatorComponent, ImageAnnotatorResult } from '../image-annotator/image-annotator.component';
import { ActionsDropdownButtonComponent } from '../actions-dropdown-button/actions-dropdown-button.component';
import { ConfirmActionModalService } from '../../services/confirm-action-modal/confirm-action-modal.service';
import { DropdownOption } from '../../models/dropdown-option.model';

/**
 * Alles, was die Lightbox von einem Dokument liest.
 *
 * Bewusst ein Struktur-Typ und nicht `WorkDocument`: dieselbe Lightbox zeigt auch die redigierten
 * Dokument-DTOs der externen Projektfreigabe, die absichtlich nur eine Teilmenge der Felder
 * tragen. `WorkDocument` erfüllt diesen Typ, bestehende Aufrufer bleiben unverändert.
 *
 * `fileSrcThumbnail` ist schreibbar, weil das Rotieren den Cache-Buster daran anhängt.
 */
export interface ViewableGalleryDocument {
  id: number;
  name?: string | null;
  mimetype?: string | null;
  fileSrc?: string | null;
  fileSrcThumbnail?: string | null;
  fileSrcOptimized?: string | null;
  created?: string | null;
  upload?: string | null;
  processingStatus?: string | null;
  projectId?: number | null;
  createdBy?: number | null;
  /** Gesetzt bei einer beschrifteten Fassung: Id des unveränderten Originals. */
  sourceDocumentId?: number | null;
  annotationJson?: string | null;
}

interface AnnotationSession {
  /** Dokument, aus dem heraus beschriftet wird (Original oder Fassung) — der Server findet die Fassung selbst. */
  documentId: number;
  imageSrc: string;
  annotationJson: string | null;
}

@Component({
  selector: 'work-ui-lightbox',
  imports: [
    PrependServerToFileSrcPipe,
    ButtonComponent,
    LoadingSpinnerComponent,
    ConfirmActionModalDirective,
    TranslateModule,
    FormatDatePipe,
    IconComponent,
    ImageAnnotatorComponent,
    ActionsDropdownButtonComponent,
  ],
  templateUrl: './lightbox.component.html',
  styleUrl: './lightbox.component.scss',
})

export class LightboxComponent implements OnDestroy {
  // Injections
  private readonly documentsV2Service = inject(WorkDocumentsV2Service);
  private readonly usersV2Service = inject(WorkUsersV2Service);
  private readonly exceptionHandlingService = inject(ExceptionHandlingService);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly renderer = inject(Renderer2);
  private readonly store = inject(Store);
  private readonly prependServerToFileSrcPipe = inject(PrependServerToFileSrcPipe);
  private readonly confirmActionModalService = inject(ConfirmActionModalService);

  @ViewChild('imageContainer', { static: false }) imageContainerRef!: ElementRef<HTMLDivElement>;
  @ViewChild('image', { static: false }) imageRef!: ElementRef<HTMLImageElement>;
  public connectedProjectNameMap = input<StringMap>();
  // image handling
  public images = input.required<ViewableGalleryDocument[]>();
  // when true, hides the info toggle, delete and rotate buttons — only download stays available
  public readonly = input<boolean>(false);
  /**
   * Blendet zusätzlich den Download aus. Nötig für loginfreie Kontexte wie die externe
   * Projektfreigabe: der Download läuft über `/v2/documents/download`, und das verlangt einen
   * angemeldeten Benutzer — ein sichtbarer Button, der 401 liefert, ist schlechter als keiner.
   */
  public hideDownload = input<boolean>(false);
  /** Schaltet das Beschriften von Fotos frei (Text, Freihand, Pfeile, Vermassungen). Das Original bleibt erhalten. */
  public annotationEnabled = input<boolean>(false);
  public currentIndex = signal<number>(0);
  public currentImage: Signal<ViewableGalleryDocument> = computed(() => {
    const currentImage = this.images()[this.currentIndex()];
    this.getUserForImage(currentImage);
    return currentImage;
  });
  // state and permission
  public canDelete = toSignal(this.store.select(ProfileState.hasPermission(WorkPermissionsEnum.DocumentDELETE)));
  public canUpdate = toSignal(this.store.select(ProfileState.hasPermission(WorkPermissionsEnum.DocumentUPDATE)));
  /* annotation */
  protected readonly annotationSession = signal<AnnotationSession | null>(null);
  protected readonly annotationSaving = signal<boolean>(false);
  /**
   * Nach dem Speichern soll die beschriftete Fassung zu sehen sein. Der Aufrufer lädt die Liste danach neu (neue
   * Fassung kommt dazu, Reihenfolge kann sich ändern) — sobald sie in `images` auftaucht, wird sie angezeigt.
   */
  private readonly documentIdToShow = signal<number | null>(null);
  private readonly showDocumentAfterReloadEffect = effect(() => {
    const documentIdToShow = this.documentIdToShow();
    const index = documentIdToShow === null ? -1 : this.images().findIndex((image) => image.id === documentIdToShow);
    if (index < 0) {
      return;
    }
    untracked(() => {
      this.currentIndex.set(index);
      this.resetZoomAndDrag();
      this.documentIdToShow.set(null);
    });
  });
  protected readonly canAnnotate = computed(
    () =>
      this.annotationEnabled() &&
      !this.readonly() &&
      !!this.canUpdate() &&
      ['image/jpeg', 'image/png', 'image/webp'].includes(this.currentImage()?.mimetype ?? ''),
  );
  /** Gegenstück zum aktuellen Bild: beim Original die beschriftete Fassung, bei der Fassung das Original. */
  protected readonly annotationCounterpart = computed(() => {
    const currentImage = this.currentImage();
    if (!currentImage) {
      return undefined;
    }
    return currentImage.sourceDocumentId
      ? this.images().find((image) => image.id === currentImage.sourceDocumentId)
      : this.images().find((image) => image.sourceDocumentId === currentImage.id);
  });
  protected readonly canRotate = computed(() => !this.readonly() && this.currentImage()?.mimetype === 'image/jpeg' && !!this.canUpdate());
  /** Aktionen im «…»-Menü der mobilen Toolbar — dieselben wie in der Desktop-Leiste, aber mit Beschriftung. */
  protected readonly mobileMenuOptions = computed<DropdownOption[]>(() => {
    const options: DropdownOption[] = [];
    if (this.annotationCounterpart()) {
      options.push({
        value: 'counterpart',
        label: this.currentImage().sourceDocumentId ? 'BILD_BESCHRIFTEN_ORIGINAL_ANZEIGEN' : 'BILD_BESCHRIFTEN_FASSUNG_ANZEIGEN',
        iconName: 'compare',
      });
    }
    if (!this.hideDownload()) {
      options.push({ value: 'download', label: 'HERUNTERLADEN', iconName: 'download' });
    }
    if (this.canRotate()) {
      options.push({ value: 'rotateLeft', label: 'BILD_NACH_LINKS_DREHEN', iconName: 'rotate_90_degrees_ccw' });
      options.push({ value: 'rotateRight', label: 'BILD_NACH_RECHTS_DREHEN', iconName: 'rotate_90_degrees_cw' });
    }
    if (!this.readonly() && this.canDelete()) {
      options.push({ value: 'delete', label: 'LOESCHEN', iconName: 'delete' });
    }
    return options;
  });
  // lightbox navigation
  public isOpen = signal(false);
  public closeAnimation = false;
  public showMetaInfo = false;
  // zoom & dragging
  public zoomLevel = 1;
  public isLoading = false;
  public documentDeleted = output<ViewableGalleryDocument>();
  /** Eine beschriftete Fassung wurde neu angelegt oder ersetzt; der Aufrufer lädt die Liste neu. */
  public documentAnnotated = output<WorkDocument>();
  public loadedUsers: WorkUserV2[] = [];
  public user: WorkUserV2 | undefined;
  protected translatePosition = { x: 0, y: 0 };
  protected readonly WorkDocumentProcessingStatusEnum = WorkDocumentProcessingStatusEnum;
  private isDragging = false;
  private dragStart = { x: 0, y: 0 };
  private lightboxDimensions = { width: 0, height: 0 };
  private imageDimensions = { width: 0, height: 0 };
  private touchStartX = 0;
  private touchEndX = 0;
  private readonly minSwipeDistance = 50;

  constructor() {
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
  }

  protected get downloadDocumentUrl(): string {
    return `${environment.apiConfiguration.baseUrl}${WorkDocumentsV2Service.DocumentsV2ControllerDownloadDocumentsPath}?ids=${
      this.currentImage().id
    }`;
  }

  protected get projectName(): string {
    const projectId = this.currentImage()?.projectId || '';
    return this.connectedProjectNameMap()?.[projectId] || '';
  }

  ngOnDestroy(): void {
    this.removeEventListeners();
  }

  public open(WorkDocument: ViewableGalleryDocument): void {
    this.renderer.setStyle(document.documentElement, 'overflow-y', 'hidden');
    this.currentIndex.set(this.images().indexOf(WorkDocument));
    this.isOpen.set(true);
    this.resetZoomAndDrag();
    this.registerEventListeners();
    // Calculate dimensions once the lightbox is visible
    setTimeout(() => this.calculateDimensions());
  }

  public close(): void {
    this.renderer.setStyle(document.documentElement, 'overflow-y', 'auto');
    this.closeAnimation = true;
    // await animation
    setTimeout(() => {
      this.isOpen.set(false);
      this.closeAnimation = false;
    }, 300);
    this.removeEventListeners();
  }

  protected navigateNext(): void {
    if (this.currentIndex() < this.images().length - 1) {
      this.currentIndex.update((index) => index + 1);
      this.resetZoomAndDrag();
    }
  }

  protected navigatePrevious(): void {
    if (this.currentIndex() > 0) {
      this.currentIndex.update((index) => index - 1);
      this.resetZoomAndDrag();
    }
  }

  protected showDocument(document: ViewableGalleryDocument): void {
    const index = this.images().indexOf(document);
    if (index < 0) {
      return;
    }
    this.currentIndex.set(index);
    this.resetZoomAndDrag();
  }

  protected async startAnnotation(): Promise<void> {
    const currentImage = this.currentImage();
    const original = currentImage.sourceDocumentId ? await this.findDocument(currentImage.sourceDocumentId) : currentImage;
    if (!original?.fileSrc) {
      return;
    }
    // Eine noch sichtbare Meldung («Beschriftung gespeichert») läge über dem Speichern-Knopf des Editors.
    this.alertDialogService.closeAllAlerts();
    // Beschriftet wird immer auf dem Original; die bestehende Zeichnung kommt aus der Fassung.
    const annotated = currentImage.sourceDocumentId ? currentImage : this.annotationCounterpart();
    this.annotationSession.set({
      documentId: currentImage.id,
      imageSrc: this.prependServerToFileSrcPipe.transform(original.fileSrc),
      annotationJson: annotated?.annotationJson ?? null,
    });
  }

  protected async saveAnnotation(result: ImageAnnotatorResult): Promise<void> {
    const annotationSession = this.annotationSession();
    if (!annotationSession) {
      return;
    }
    this.annotationSaving.set(true);
    try {
      const annotatedDocument = await lastValueFrom(
        this.documentsV2Service.documentsV2ControllerSaveAnnotation({
          id: annotationSession.documentId,
          body: { file: result.blob, annotationJson: result.annotationJson },
        }),
      );
      this.annotationSession.set(null);
      // Eine ersetzte Fassung hat neue Dateinamen (siehe API) — bis die Liste neu geladen ist, direkt übernehmen.
      const replacedImage = this.images().find((image) => image.id === annotatedDocument.id);
      if (replacedImage) {
        Object.assign(replacedImage, {
          name: annotatedDocument.name,
          fileSrc: annotatedDocument.fileSrc,
          fileSrcOptimized: annotatedDocument.fileSrcOptimized,
          fileSrcThumbnail: annotatedDocument.fileSrcThumbnail,
          annotationJson: annotatedDocument.annotationJson,
        });
      }
      this.documentIdToShow.set(annotatedDocument.id);
      this.documentAnnotated.emit(annotatedDocument);
      this.alertDialogService.showAlert({
        title: 'BILD_BESCHRIFTEN_GESPEICHERT',
        feedbackState: 'success',
      });
    } catch (saveAnnotationException) {
      this.exceptionHandlingService.handleExceptionWithAlert('saveAnnotationException', saveAnnotationException);
    } finally {
      this.annotationSaving.set(false);
    }
  }

  protected handleMouseDown(event: MouseEvent): void {
    if (this.zoomLevel > 1) {
      this.isDragging = true;
      this.dragStart = { x: event.clientX, y: event.clientY };

      window.addEventListener('mousemove', this.handleMouseMove);
      window.addEventListener('mouseup', this.handleMouseUp);
    }
  }

  protected handleTouchStart(event: TouchEvent): void {
    this.touchStartX = event.changedTouches[0].screenX;
  }

  protected handleTouchEnd(event: TouchEvent): void {
    this.touchEndX = event.changedTouches[0].screenX;
    this.handleSwipe();
  }

  protected async selectMobileMenuAction(option: DropdownOption): Promise<void> {
    switch (option.value) {
      case 'counterpart': {
        const counterpart = this.annotationCounterpart();
        if (counterpart) {
          this.showDocument(counterpart);
        }
        break;
      }
      case 'download':
        window.open(this.downloadDocumentUrl, '_blank');
        break;
      case 'rotateLeft':
        await this.rotateImage(true);
        break;
      case 'rotateRight':
        await this.rotateImage();
        break;
      case 'delete': {
        const confirmButton = await firstValueFrom(this.confirmActionModalService.showModal());
        if (confirmButton && confirmButton.role !== 'cancel') {
          await this.delete();
        }
        break;
      }
    }
  }

  protected async rotateImage(counterClockWise = false) {
    this.isLoading = true;
    try {
      await lastValueFrom(
        this.documentsV2Service.documentsV2ControllerRotate({
          id: this.currentImage().id,
          counterclockwise: counterClockWise,
        }),
      );
      this.bustImageCache(this.currentImage());
      this.isLoading = false;
    } catch (documentRotateException) {
      this.exceptionHandlingService.handleExceptionWithAlert('documentRotateException', documentRotateException);
      this.isLoading = false;
    }
  }

  protected async delete() {
    try {
      await lastValueFrom(
        this.documentsV2Service.documentsV2ControllerDelete({
          id: this.currentImage().id,
        }),
      );
      this.documentDeleted.emit(this.currentImage());
      this.alertDialogService.showAlert({
        title: 'LOESCHEN_ERFOLG',
        feedbackState: 'success',
      });
    } catch (documentDeleteException) {
      this.exceptionHandlingService.handleExceptionWithAlert('documentDeleteException', documentDeleteException);
    }
  }

  /** Erzwingt das Neuladen der Bilddateien, wenn sie unter gleichem Namen überschrieben wurden. */
  private bustImageCache(image: ViewableGalleryDocument): void {
    const time = new Date().getTime();
    image.fileSrc = image.fileSrc + '?' + time;
    image.fileSrcOptimized = image.fileSrcOptimized + '?' + time;
    image.fileSrcThumbnail = image.fileSrcThumbnail + '?' + time;
  }

  private async findDocument(documentId: number): Promise<ViewableGalleryDocument | undefined> {
    const loadedDocument = this.images().find((image) => image.id === documentId);
    if (loadedDocument) {
      return loadedDocument;
    }
    try {
      const documents = await lastValueFrom(this.documentsV2Service.documentsV2ControllerFindDocuments({ id: documentId }));
      return documents[0];
    } catch (findDocumentException) {
      this.exceptionHandlingService.handleExceptionWithAlert('findDocumentException', findDocumentException);
      return undefined;
    }
  }

  private getUserForImage(currentImage: ViewableGalleryDocument) {
    const userId = currentImage.createdBy;
    if (!userId) {
      return;
    }
    this.user = this.loadedUsers.find((user) => user.id === userId);
    if (!this.user) {
      this.loadUser(userId).then();
    }
  }

  private async loadUser(userId: number | null) {
    if (userId === null) {
      return;
    }
    try {
      this.user = await lastValueFrom(
        this.usersV2Service.usersV2ControllerFindOneById({
          id: userId,
        }),
      );
      this.loadedUsers.push(this.user);
    } catch (loadUserException) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadUserException', loadUserException);
    }
  }

  private registerEventListeners(): void {
    window.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('wheel', this.handleScroll);
    window.addEventListener('resize', this.calculateDimensions.bind(this));
  }

  private removeEventListeners(): void {
    window.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('wheel', this.handleScroll);
    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('mouseup', this.handleMouseUp);
    window.addEventListener('resize', this.calculateDimensions.bind(this));
  }

  private handleKeydown(event: KeyboardEvent): void {
    // Der Editor hat eigene Tastenkürzel (ESC, Undo); die Lightbox darunter bleibt dann still.
    if (this.annotationSession()) {
      return;
    }
    switch (event.key) {
      case 'ArrowRight':
        this.navigateNext();
        break;
      case 'ArrowLeft':
        this.navigatePrevious();
        break;
      case 'Escape':
        this.close();
        break;
    }
  }

  private handleScroll(event: WheelEvent): void {
    if (this.annotationSession()) {
      return;
    }
    const zoomStep = 0.1;
    const maxZoom = 3;
    const minZoom = 1;
    if (event.deltaY < 0) {
      // Scrolling up -> zoom in
      this.zoomLevel = Math.min(this.zoomLevel + zoomStep, maxZoom);
    } else {
      // Scrolling down -> zoom out
      this.zoomLevel = Math.max(this.zoomLevel - zoomStep, minZoom);
    }

    // If zoomLevel is reset to 1, also reset drag position
    if (this.zoomLevel === 1) {
      this.translatePosition = { x: 0, y: 0 };
    }

    // Recalculate lightbox and image dimensions as zoom level changes
    this.calculateDimensions();
  }

  /**
   * Handle mouse movement (dragging).
   * Constrain the translation to calculated drag boundaries based on current zoom level and dimensions.
   */
  private handleMouseMove(event: MouseEvent): void {
    if (this.isDragging && this.zoomLevel > 1) {
      const deltaX = event.clientX - this.dragStart.x;
      const deltaY = event.clientY - this.dragStart.y;

      // Dynamically calculate overflow and update translate position
      const boundariesX = this.getDragBoundaries('x');
      const boundariesY = this.getDragBoundaries('y');

      this.translatePosition = {
        x: this.clampTranslate(this.translatePosition.x + deltaX, boundariesX.min, boundariesX.max),
        y: this.clampTranslate(this.translatePosition.y + deltaY, boundariesY.min, boundariesY.max),
      };

      // Update drag start position to current mouse position
      this.dragStart = { x: event.clientX, y: event.clientY };
    }
  }

  private handleMouseUp(): void {
    this.isDragging = false;
    // Remove the event listeners for dragging
    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('mouseup', this.handleMouseUp);
  }

  private handleSwipe(): void {
    const swipeDistance = this.touchEndX - this.touchStartX;

    if (Math.abs(swipeDistance) < this.minSwipeDistance) {
      return;
    }

    if (swipeDistance > 0 && this.currentIndex() > 0) {
      // Swipe nach rechts - vorheriges Bild
      this.navigatePrevious();
    } else if (swipeDistance < 0 && this.currentIndex() < this.images().length - 1) {
      // Swipe nach links - nächstes Bild
      this.navigateNext();
    }
  }

  /**
   * Calculate dimensions of the lightbox container and image.
   */
  private calculateDimensions(): void {
    if (this.imageContainerRef?.nativeElement && this.imageRef?.nativeElement) {
      const lightboxElement = this.imageContainerRef.nativeElement;
      const imageElement = this.imageRef.nativeElement;

      const setDimensions = (lightboxElement: HTMLElement, imageElement: HTMLImageElement): void => {
        // Lightbox dimensions from the DOM
        const lightboxWidth = lightboxElement.offsetWidth;
        const lightboxHeight = lightboxElement.offsetHeight;

        // Image's natural dimensions
        const imageNaturalWidth = imageElement.naturalWidth;
        const imageNaturalHeight = imageElement.naturalHeight;

        // Calculate the scale factor to fit the image within the lightbox
        const widthScale = lightboxWidth / imageNaturalWidth;
        const heightScale = lightboxHeight / imageNaturalHeight;
        const scaleFactor = Math.min(widthScale, heightScale, 1); // Scale down, but don't upscale

        // Constrain image dimensions to fit within the lightbox
        this.imageDimensions = {
          width: imageNaturalWidth * scaleFactor, // Scaled width
          height: imageNaturalHeight * scaleFactor, // Scaled height
        };

        // Lightbox dimensions remain the same
        this.lightboxDimensions = {
          width: lightboxWidth,
          height: lightboxHeight,
        };
      };

      // Wait for the image to load to get accurate dimensions
      if (imageElement.complete) {
        // If the image is already loaded, fetch dimensions immediately
        setDimensions(lightboxElement, imageElement);
      } else {
        // Otherwise, listen for the image load event
        imageElement.addEventListener('load', () => {
          setDimensions(lightboxElement, imageElement);
        });
      }
    }
  }

  /**
   * Calculate drag boundaries for a given axis ('x' or 'y').
   */
  private getDragBoundaries(axis: 'x' | 'y'): { min: number; max: number } {
    // Image dimensions after applying zoom
    const zoomedImageSize = {
      width: this.imageDimensions.width * this.zoomLevel,
      height: this.imageDimensions.height * this.zoomLevel,
    };

    // Lightbox dimensions
    const lightboxSize = this.lightboxDimensions;

    // Calculate overflow (how much the zoomed image exceeds the container dimensions)
    const overflow =
      axis === 'x'
        ? zoomedImageSize.width - lightboxSize.width // Horizontal overflow
        : zoomedImageSize.height - lightboxSize.height; // Vertical overflow

    // If the zoomed image is smaller than or equal to the lightbox, prevent dragging
    if (overflow <= 0) {
      return { min: 0, max: 0 };
    }

    // Otherwise, calculate min and max drag boundaries
    const maxOffset = overflow / 2 / this.zoomLevel; // Divide by 2 since the image is centered

    return { min: -maxOffset, max: maxOffset };
  }

  /**
   * Clamp a translation value to stay within min and max bounds.
   */
  private clampTranslate(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
  }

  private resetZoomAndDrag(): void {
    this.zoomLevel = 1;
    this.translatePosition = { x: 0, y: 0 };
  }
}
